import { useCallback, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { Tabs, DatePicker, Select } from "antd";
import Boton from "../Components/UI/Boton";
import { IconoBuscar } from "../Components/UI/Iconos";
import GridFacturaListadoPorCobrar from "../Components/Factura/Listar/GridFacturaListadoPorCobrar";
import GridSinDistribuir from "../Components/Factura/Listar/GridSinDistribuir";
import { GetTokenDecoded } from "../utils/storeUsuario";
import { ControllerListarSegurosClinica } from "../Service/Services.seguros";
import { ListarEspecialidadClinicaActivas } from "../Service/Service.especialidad";
import { GetExamenesAll } from "../Service/Service.examen";
import { GetPacientesAll } from "../Service/Service.paciente";

const { RangePicker } = DatePicker;

// COBERTURAS-09 -- módulo operativo de aseguradoras.
//   Filtros SIEMPRE visibles sobre el grid (regla UX del módulo, ver
//   context/17). Dos vistas: "Sin distribuir" (09A) y "Pendiente de facturar".
const EMPTY = {
    rangoFechas: null,
    fecha_inicial: undefined,
    fecha_final: undefined,
    aseguradora: undefined,
    especialidad: undefined,
    paciente: undefined,
    examen: undefined,
    estado: "Por Cobrar",
};

function FacturaListarPorCobrar() {
    const [tab, setTab] = useState("sin-distribuir");

    // filtros que edita el usuario (draft) vs los aplicados al grid
    const [draft, setDraft] = useState(EMPTY);
    const [applied, setApplied] = useState({ ...EMPTY });

    const [seguros, setSeguros] = useState([]);
    const [especialidades, setEspecialidades] = useState([]);
    const [examenes, setExamenes] = useState([]);
    const [pacientes, setPacientes] = useState([]);

    const loadCatalogos = useCallback(async () => {
        const idclinica = GetTokenDecoded()?.idclinica;
        const [seg, esp] = await Promise.all([
            ControllerListarSegurosClinica(idclinica),
            ListarEspecialidadClinicaActivas(idclinica),
        ]);
        setSeguros(
            (seg || [])
                .filter((s) => (s.tipoSeguro?.tipo || "").toUpperCase() !== "PARTICULAR")
                .map((s) => ({ value: s.idseguro, label: s.nombre }))
        );
        const espData = esp?.data || esp || [];
        setEspecialidades(
            (Array.isArray(espData) ? espData : []).map((e) => ({
                value: e.idespecialidad,
                label: e.especialidad,
            }))
        );
    }, []);

    useEffect(() => {
        loadCatalogos();
    }, [loadCatalogos]);

    const buscarExamenes = async (search) => {
        const r = await GetExamenesAll(1, 15, search || "");
        if (r?.success) {
            setExamenes(r.data.map((d) => ({ value: d.IdExamenTipo, label: `${d.codigo} - ${d.descripcion}` })));
        }
    };
    const buscarPacientes = async (search) => {
        const r = await GetPacientesAll(1, 15, search || "");
        if (r?.success) {
            setPacientes(r.data.map((d) => ({ value: d.idpaciente, label: `${d.cedula} - ${d.nombres_completos}` })));
        }
    };

    const aplicar = () => {
        const [d0, d1] = draft.rangoFechas || [];
        setApplied({
            ...draft,
            fecha_inicial: d0 ? d0.format("YYYY-MM-DD") : undefined,
            fecha_final: d1 ? d1.format("YYYY-MM-DD") : undefined,
            // GridFacturaListadoPorCobrar espera `seguro`; 09A espera `aseguradora`.
            seguro: draft.aseguradora,
        });
    };

    const limpiar = () => {
        setDraft(EMPTY);
        setApplied({ ...EMPTY });
    };

    useEffect(() => {
        // primera carga
        aplicar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="page-content">
            <div className="container-fluid">
                <Row className="mb-2">
                    <Col>
                        <ol className="breadcrumb m-0">
                            <li className="breadcrumb-item">Facturación a aseguradoras</li>
                            <li className="breadcrumb-item active">Por cobrar</li>
                        </ol>
                    </Col>
                </Row>

                {/* ---- FILTROS SIEMPRE VISIBLES ---- */}
                <Row className="g-2 align-items-end mb-3 p-2 border rounded bg-light">
                    <Col xs={12} md={4} lg={3}>
                        <label className="kc__etiqueta"><small>Rango de fechas</small></label>
                        <RangePicker
                            className="w-100"
                            value={draft.rangoFechas}
                            onChange={(dates) => setDraft((f) => ({ ...f, rangoFechas: dates }))}
                            getPopupContainer={() => document.body}
                        />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta"><small>Aseguradora</small></label>
                        <Select
                            className="w-100"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder="Todas"
                            options={seguros}
                            value={draft.aseguradora}
                            onChange={(v) => setDraft((f) => ({ ...f, aseguradora: v }))}
                        />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta"><small>Especialidad</small></label>
                        <Select
                            className="w-100"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder="Todas"
                            options={especialidades}
                            value={draft.especialidad}
                            onChange={(v) => setDraft((f) => ({ ...f, especialidad: v }))}
                        />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta"><small>Paciente</small></label>
                        <Select
                            className="w-100"
                            allowClear
                            showSearch
                            filterOption={false}
                            placeholder="Buscar paciente"
                            options={pacientes}
                            value={draft.paciente}
                            onSearch={buscarPacientes}
                            onChange={(v) => setDraft((f) => ({ ...f, paciente: v }))}
                        />
                    </Col>
                    <Col xs={6} md={4} lg={2}>
                        <label className="kc__etiqueta"><small>Servicio</small></label>
                        <Select
                            className="w-100"
                            allowClear
                            showSearch
                            filterOption={false}
                            placeholder="Buscar servicio"
                            options={examenes}
                            value={draft.examen}
                            onSearch={buscarExamenes}
                            onChange={(v) => setDraft((f) => ({ ...f, examen: v }))}
                        />
                    </Col>
                    {tab === "pendiente" && (
                        <Col xs={6} md={4} lg={2}>
                            <label className="kc__etiqueta"><small>Estado</small></label>
                            <Select
                                className="w-100"
                                options={[
                                    { value: "Por Cobrar", label: "Por Cobrar" },
                                    { value: "Facturado", label: "Facturado" },
                                    { value: "Eliminado", label: "Eliminado" },
                                ]}
                                value={draft.estado}
                                onChange={(v) => setDraft((f) => ({ ...f, estado: v }))}
                            />
                        </Col>
                    )}
                    <Col xs={12} lg={3} className="d-flex gap-2">
                        <Boton variante="primario" icono={IconoBuscar} onClick={aplicar} data-testid="pc-buscar">
                            Buscar
                        </Boton>
                        <Boton onClick={limpiar} data-testid="pc-limpiar">
                            Limpiar
                        </Boton>
                    </Col>
                </Row>

                <Tabs
                    activeKey={tab}
                    onChange={setTab}
                    items={[
                        {
                            // COBERTURAS-FLOW-FORWARD-02 seccion 17-24: "Pendientes por
                            // procesar" es la vista operativa amplia -- MISMO endpoint/
                            // query que "Sin distribuir" (09A), solo reframed con
                            // Clínica/Plan/Tipo de situación/Motivo y una Acción que
                            // distingue forward (Ir a Crear factura) de excepción
                            // (Generar cobertura). Va primero porque es el flujo de
                            // trabajo diario; "Sin distribuir" queda como bandeja
                            // histórica/de excepción (mismos datos, columnas originales).
                            key: "pendientes-procesar",
                            label: "Pendientes por procesar",
                            children: <GridSinDistribuir data={{ filters: applied, mode: "pendientes-procesar" }} />,
                        },
                        {
                            key: "sin-distribuir",
                            label: "Sin distribuir",
                            children: <GridSinDistribuir data={{ filters: applied }} />,
                        },
                        {
                            key: "pendiente",
                            label: "Pendiente de facturar",
                            children: <GridFacturaListadoPorCobrar data={{ filters: applied }} />,
                        },
                    ]}
                />
            </div>
        </div>
    );
}

export default FacturaListarPorCobrar;
